/**
 * The album preview of the desktop editor (design handoff "Aperçu 3D de l'album") : the album on a
 * wooden table, its pages turning in 3D (desktop.common.preview.BookTable), "Retour à l'éditeur"
 * and "Commander · price" in the top right corner, the format and the cover under the title — a
 * click opens the info panel to change them.
 *
 * Every side of every spread is drawn as a picture by desktop.common.preview.PageArtwork, the
 * closest to the page shown first : the leaves lie blank until their picture is there.
 *
 * Faces, in book order : the front cover, then each inner spread's left and right sides, then the
 * back cover. The first spread's left and the last spread's right are the inside covers, which are
 * not printed (hasbackcover) : they are left blank.
 */
fx.ns("desktop.common.preview", class Preview extends fx.components.PopupPanel {

	constructor()
	{
		super();

		this.destroyOnSwitch = true;

		// no "Revenir" button of PopupPanel's own : the table has its "Retour à l'éditeur"
		this.mode = "none";
	}

	// the drawn pages are as sharp as the page shows on this screen (its size on the table times the
	// screen's pixel density : twice on a Retina screen), from ARTWORK_MIN to ARTWORK_MAX pixels for
	// their widest side. Drawn smaller, the browser scales them up and the texts go soft
	get ARTWORK_MIN() { return 1000; }
	get ARTWORK_MAX() { return 2400; }

	// a page drawn incomplete (a photo slow to load) is drawn again once every other page is there,
	// after a pause, as many times at most : the photos it waited for keep loading meanwhile, and
	// come from the browser's cache the next time. The pause keeps a photo that fails at once (gone
	// from the server) from drawing its page in a loop
	get REDRAWS() { return 10; }
	get REDRAW_PAUSE() { return 3000; }

	// how often the pages waiting for photos being sent look at their upload (ms)
	get UPLOAD_WATCH() { return 400; }

	// the progress bar of the page being drawn : each photo there moves it on by its share, and
	// while one loads the bar creeps towards the next share at this pace (ms), never reaching it
	get PROGRESS_PACE() { return 2500; }

	// a page with nothing printed (the inside covers)
	get PLAIN() { return "#fffdf8"; }

	get book()
	{
		return this.data.creation;
	}

	// --------------------------- RENDER --------------------------- //

	create()
	{
		super.create();

		this.table = new desktop.common.preview.BookTable();
		this.addChild(this.table, {width : "100%", height : "100%", top : 0, left : 0});
	}

	onReady()
	{
		super.onReady();

		this.on("back", this.onBackClick, this.table);
		this.on("order", this._onOrder, this.table);
		this.on("options", this._onInfoPanel, this.table);
		this.on("spread", this._onSpread, this.table);
		this.on("ESCAPE", this.onBackClick, fx.keyboard);

		// seeing the preview is not a modification : the creation is undeleted by the editor on
		// the first one, and here by the order, never by the mere opening of this screen.

		this.listenUpdates(this.book, true);

		this.table.labelOf = this._label.bind(this);
		this._build();
		this._updateTexts();

		// the preview is the last look at the album before ordering : invite the user to go
		// through it, once the album has had a second to show
		if (!this.data.account.wasNotificated("preview-ready"))
			this.delay(this._showPreviewReady, 1000);
	}

	// once for good : remembered on the account like the other help bubbles. Leaving the
	// preview within the second destroys this screen, and the delay with it
	_showPreviewReady()
	{
		this.data.account.notificated("preview-ready");
		this.popup(new common.popups.PreviewReady());
	}

	// the faces of the book and their pictures, from scratch : at the opening, and when the format or the cover changes
	_build()
	{
		let inner = this.book.pages.toArray().filter((page) => !page.isCover),
			cover = this.book.cover;

		this._spreads = inner;
		this._faces = [{ element : cover, side : "right", cover : true }];

		inner.forEach((page) => {
			let insideCover = page.hasbackcover;
			this._faces.push({ element : page, side : "left", plain : insideCover && page.isFirstPage });
			this._faces.push({ element : page, side : "right", plain : insideCover && !page.isFirstPage });
		});

		this._faces.push({ element : cover, side : "left", cover : true });

		this._binding = new desktop.common.preview.BookBinding(this.book);
		this.table.build(this._faces, this._binding);

		if (this._artwork)
		{
			this._artwork.abort();
			this._artwork.destroy();
		}

		this._artwork = new desktop.common.preview.PageArtwork(this, this.data, this._artworkRatio());
		// a blank page is drawn too on a spiral : it is punched like the others
		this._pending = this._faces.map((face, index) => index).filter((index) => !this._faces[index].plain || this._binding.spiral);
		this._redraws = [];
		this._sharpen = [];
		this._redrawSoon = false;
		this._visited = [this.table.spread];
		this._drawing = null;
		this._drawNext();
	}

	// pixels per millimetre of the drawn pages
	_artworkRatio()
	{
		let widest = Math.max(this.book.pageWidth, this.book.pageHeight),
			shown = this.table.PAGE_WIDTH * (this.table.faceScale || 1) * (window.devicePixelRatio || 1) * widest / this.book.pageWidth;

		return Math.max(this.ARTWORK_MIN, Math.min(this.ARTWORK_MAX, shown)) / widest;
	}

	// the spread showing a face
	_spreadOf(index)
	{
		return Math.floor((index + 1) / 2);
	}

	// the order the faces are drawn in : the spreads turned to, the last one first — turning five
	// pages quickly draws the fifth, then the fourth… back to the first —, then the others, the
	// closest to the spread on the table first
	_rank(index)
	{
		let spread = this._spreadOf(index),
			visited = this._visited.indexOf(spread);

		return visited >= 0 ? visited : this._visited.length + Math.abs(spread - this.table.spread);
	}

	// draws the face ranked first, then the next one, until all are drawn. The photos come in their
	// medium version first, the one the editor shows : quick to load, every page is there soon. A
	// page where one is drawn larger than that version is drawn again at full resolution (_sharpen),
	// once every page is there — the spread on the table straight after its own pages
	_drawNext()
	{
		if (this._drawing || !this._artwork)
			return;

		// the pages drawn incomplete once every other is there, after a pause
		if (!this._pending.length && this._redraws.length && !this._redrawSoon)
		{
			this._redrawSoon = true;
			this.delay(this._redraw, this.REDRAW_PAUSE);
		}

		let best = (list) => list.length ? list.reduce((best, index) => this._rank(index) < this._rank(best) ? index : best) : null,
			first = best(this._pending),
			sharp = best(this._sharpen),
			full = sharp !== null && (first === null || (this._spreadOf(sharp) == this.table.spread && this._rank(first) > 0)),
			next = full ? sharp : first;

		if (next === null)
			return;

		let queue = full ? this._sharpen : this._pending;
		queue.splice(queue.indexOf(next), 1);
		this._drawing = { index : next };

		let artwork = this._artwork,
			face = this._faces[next];

		// the binding is on the left of a right page, on the right of a left one : its shadow (none on
		// the covers nor on a spiral's sheets, apart from each other) and a spiral's holes
		let edge = (face.side == "right") ? "left" : "right",
			options = {
				fold : (face.cover || this._binding.spiral) ? "" : edge,
				holes : this._binding.holes(edge),
				full : full,
			},
			ratio = artwork.ratio;

		if (!face.plain && !full)
		{
			this._progress = { index : next, zone : face.zone, done : 0, total : 0, since : Date.now() };
			options.progress = (done, total) => this._onProgress(next, done, total);
			this._tickProgress();
			this.repeat(this._tickProgress, 100);
		}

		let drawing = face.plain ?
				artwork.blank(this.book.pageWidth * ratio, this.book.pageHeight * ratio, this.PLAIN, options) :
				artwork.draw(face.element, face.side, options);

		drawing.then((url) => {
			if (artwork != this._artwork)
				return;

			if (!full)
				this._stopProgress(next);

			// given up on for the spread turned to : drawn again when its turn comes
			if (artwork.aborted)
			{
				(full ? this._sharpen : this._pending).push(next);
				this._drawing = null;
				this._drawNext();
				return;
			}

			// at full resolution : shown only once whole, the medium one stays otherwise
			if (full)
			{
				if (url && artwork.complete)
					this.table.image(next, url, false);

				this._drawing = null;
				this._drawNext();
				return;
			}

			if (url && artwork.complete && artwork.upscaled)
				this._sharpen.push(next);

			// an incomplete picture is still shown, better than a blank page, until the next one : the
			// photos still loading are cut out of it, and blink there
			if (url)
				this.table.image(next, url, !face.plain && !artwork.complete);

			// the bar of a page drawn incomplete waits in the photo still loading
			if (url && !face.plain && !artwork.complete)
			{
				face.zone = this._zoneOf(artwork.missing[0]);
				this.table.progress(next, 0, face.zone);
			}

			// photos still being sent : drawn again once they are on the server, the bar following
			// their upload meanwhile (_watchUploads), with no retry wasted on them
			if (!face.plain && !artwork.complete && artwork.waiting.length && artwork.waiting.length == artwork.missing.length)
			{
				face.waiting = artwork.waiting.slice();
				this.repeat(this._watchUploads, this.UPLOAD_WATCH);
			}
			else
			if (!face.plain && !artwork.complete && (face.redrawn || 0) < this.REDRAWS)
			{
				face.redrawn = (face.redrawn || 0) + 1;
				this._redraws.push(next);

				// the page on the table is drawn again after the pause, not after all the others
				if (this._spreadOf(next) == this.table.spread)
					this.delay(this._redrawShown, this.REDRAW_PAUSE);
			}

			this._drawing = null;
			this._drawNext();
		});
	}

	_onProgress(index, done, total)
	{
		if (!this._progress || this._progress.index != index)
			return;

		this._progress.done = done;
		this._progress.total = total;
		this._progress.since = Date.now();
		this._tickProgress();
	}

	_tickProgress()
	{
		let p = this._progress;
		if (!p)
			return this.repeat(this._tickProgress, null);

		let creep = 0.9 * (1 - Math.exp(-(Date.now() - p.since) / this.PROGRESS_PACE)),
			value = p.total ? Math.min(p.total, p.done + (p.done < p.total ? creep : 0)) / p.total : creep;

		this.table.progress(p.index, value, p.zone);
	}

	// the drawing of that face is over : the bar is left at 0 for the next one (aborted, incomplete)
	_stopProgress(index)
	{
		if (this._progress && this._progress.index == index)
		{
			this._progress = null;
			this.repeat(this._tickProgress, null);
		}

		this.table.progress(index, 0, this._faces[index] && this._faces[index].zone);
	}

	// a missing photo's zone (ThumbGenerator) → its centre and width, shares of the page
	_zoneOf(zone)
	{
		if (!zone)
			return null;

		// in page widths, the height being aspect of them : the rectangle turns on its top left corner
		let aspect = this.book.pageHeight / this.book.pageWidth,
			angle = (zone.rotation || 0) * Math.PI / 180,
			w = zone.width / 2,
			h = zone.height * aspect / 2,
			cx = zone.x + w * Math.cos(angle) - h * Math.sin(angle),
			cy = zone.y * aspect + w * Math.sin(angle) + h * Math.cos(angle);

		return { cx : cx, cy : cy / aspect, width : zone.width };
	}

	// the pages waiting for photos being read or sent : their bar follows the upload, and once the
	// browser has a picture of every one of them (read : its local copy ; a HEIC : on the server), the
	// page goes back in the queue
	_watchUploads()
	{
		if (!this._artwork)
			return this.repeat(this._watchUploads, null);

		let watching = false;

		this._faces.forEach((face, index) => {
			if (!face.waiting)
				return;

			if (face.waiting.every((photo) => shared.components.CoverThumbPhoto.showable(photo) || photo.deleted || photo.destroyed))
			{
				face.waiting = null;

				if (this._redraws.indexOf(index) >= 0)
					this._redraws.splice(this._redraws.indexOf(index), 1);

				if (this._pending.indexOf(index) < 0 && !(this._drawing && this._drawing.index == index))
					this._pending.push(index);

				return;
			}

			watching = true;

			let sent = face.waiting.map((photo) => {
				let upload = photo._upload;
				return (upload && upload.size) ? Math.min(1, (upload.loaded || 0) / upload.size) : 0;
			});

			// the server still has to make its JPEG once the file is there : never quite full
			this.table.progress(index, 0.9 * sent.reduce((sum, value) => sum + value, 0) / sent.length, face.zone);
		});

		if (!watching)
			this.repeat(this._watchUploads, null);

		this._drawNext();
	}

	// the pages of the spread on the table drawn incomplete, drawn again first
	_redrawShown()
	{
		this._takeRedraws(this.table.spread);
		this._drawNext();
	}

	// the faces of a spread waiting to be drawn again, back in the queue
	_takeRedraws(spread)
	{
		this._redraws.filter((index) => this._spreadOf(index) == spread).forEach((index) => {
			this._redraws.splice(this._redraws.indexOf(index), 1);
			this._pending.push(index);
		});
	}

	_redraw()
	{
		this._redrawSoon = false;
		this._pending = this._pending.concat(this._redraws.splice(0));
		this._drawNext();
	}

	// draws the cover again : its options changed
	_redrawCover()
	{
		[0, this._faces.length - 1].forEach((index) => {
			if (this._pending.indexOf(index) < 0)
				this._pending.unshift(index);
		});

		this._drawNext();
	}

	// "Couverture", "Page 1", "Pages 2 – 3", …, "Dos"
	_label(spread)
	{
		if (spread == 0)
			return fx.text("Couverture");

		if (spread > this._spreads.length)
			return fx.text("Dos");

		let page = this._spreads[spread - 1],
			numbers = ["left", "right"].map((side) => (page.label(side).match(/\d+/) || [])[0]).filter(Boolean);

		if (numbers.length == 2)
			return fx.text("Pages") + " " + numbers[0] + " – " + numbers[1];

		return numbers.length ? fx.text("Page") + " " + numbers[0] : "";
	}

	_updateTexts()
	{
		let currentCover = this.book.covers.find({code : this.book.cover_option}),
			format = this.data.creationFormat,
			pages = this.book.numberPages;

		this.table.texts({
			title : fx.text("Votre album"),
			subtitle : pages + " " + fx.text("pages") + " · ",
			options : fx.text("Album") + " " + format.description + (currentCover ? ", " + currentCover.label : ""),
			order : fx.text("Commander") + " · " + this.book.totalPrice,
		});
	}

	onChildUpdated(action)
	{
		this.onDataUpdated(action);
	}

	onDataUpdated(action)
	{
		// the "init" one comes before onReady, which builds the table
		if (!this._faces)
			return;

		// the format changes every page, the cover option the album's thickness, its covers and binding
		if (action.impacts("formatid") || action.impacts("cover_option"))
			this._build();

		this._updateTexts();
	}

	onDataEvent(event)
	{
		if (event.type == "cover-updated")
			this._redrawCover();
	}

	// --------------------------- EVENTS --------------------------- //

	_onSpread()
	{
		let spread = this.table.spread;

		this._visited = [spread].concat(this._visited.filter((visited) => visited != spread));

		// its pages drawn incomplete are drawn again now, rather than after all the others
		this._takeRedraws(spread);

		// a page turned past stops loading its photos : the spread on the table goes first
		if (this._drawing && this._spreadOf(this._drawing.index) != spread)
			this._artwork.abort();

		this._drawNext();
	}

	// override
	onBackClick()
	{
		this.data.event("show-editor");
	}

	close()
	{
		super.close();

		this.data.event("show-editor");
	}

	_onInfoPanel()
	{
		this.data.event("info-panel");
	}

	_onOrder()
	{
		// ordering is a commitment : the album has to exist in the account even if the client
		// changed nothing, a deleted creation being refused by the cart.
		this.data.undelete();

		this.data.event("order-confirmed");
	}

	destroy()
	{
		if (this._artwork)
		{
			this._artwork.abort();
			this._artwork.destroy();
		}

		this._artwork = null;

		super.destroy();

		this.table.destroy();
	}
});
